import {
  NG_VALUE_ACCESSOR,
  FormGroup,
  FormBuilder,
  Validators,
  ControlValueAccessor,
} from "@angular/forms";
import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
} from "@angular/core";
import { ValorizationEdit } from "../../../../model/valorization-edit";
import { DocumentationItem } from "../../../../model/documentation-item";
import { PracticeRegionSearchReservation } from "../../../../model/practice-region-reservation";
import { ValorizationService } from "../../../../services/valorization/valorization.service";
import { NotificationToastService } from "../../../../services/notification-toast.service";
import { ValorizationItemAmount } from "../../../../model/valorization-item";
import { NormativeService } from "../../../../services/normative/normative.service";
import { MatDialog } from "@angular/material";
import { NormativeDialogComponent } from "../../../dialogs/normative/normative-dialog.component";
import { ReceivedLabSampleComponent } from "../../../dialogs/received-lab-sample/received-lab-sample.component";
import { ReceivedLabSampleService } from "../../../../services/received-lab-sample/received-lab-sample.service";
import { MedicalAppointment } from "../../../../model/medical-appointment";
import { ReceivedLabSampleDialogComponent } from "../../../dialogs/received-lab-sample-dialog/received-lab-sample-dialog.component";
import { ValorizationAmountControlComponent } from "../valorization-amount/valorization-amount-control.component";

export const VALORIZATION_EDIT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ValorizationEditControlComponent),
  multi: true,
};

@Component({
  selector: "app-valorization-edit-control",
  providers: [VALORIZATION_EDIT_CONTROL_VALUE_ACCESSOR],
  templateUrl: "./valorization-edit-control.component.html",
  styleUrls: ["./valorization-edit-control.component.css"],
})
export class ValorizationEditControlComponent implements ControlValueAccessor {
  @Input() loading: boolean;
  @Input() isRegistered: boolean;
  @Input() editableAgreementPriceIds: number[] = [];
  @Input() isDisabled: boolean = false;
  onChange;
  receivedLabSampleEnabled: boolean;
  findingNormative: boolean;
  recalculating: boolean = false;

  form: FormGroup;
  // tslint:disable-next-line:no-output-on-prefix
  @Output() onRecalculateClick = new EventEmitter();
  totalDocumentationItems: number;

  @Output() onRefreshPracticeClick = new EventEmitter();
  @Output() onAddPracticeClick = new EventEmitter();

  @ViewChild(ValorizationAmountControlComponent, { static: false }) valorizationAmountControl: ValorizationAmountControlComponent;

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private receivedLabSampleService: ReceivedLabSampleService,
    private normativeService: NormativeService,
    private notificationToastService: NotificationToastService,
    private valorizationService: ValorizationService
  ) {
    this.form = this.formBuilder.group({
      ValorizationAmount: [
        { value: null, disabled: this.isDisabled },
        Validators.required,
      ],
      DocumentationItems: [
        { value: null, disabled: this.isDisabled },
        Validators.required,
      ],
    });

    this.form.valueChanges.subscribe(() => {
      this.changeValorizationEdit();
    });
  }

  changeValorizationEdit() {
    const valorizationEdit = <ValorizationEdit>this.form.getRawValue();
    if (this.onChange) {
      this.onChange(valorizationEdit);
    }
  }

  async addSetInsumoClick($event: any) {
    try {
      this.loading = true;
      const item = $event.item;
      const agreementPriceIds = $event.agreementPriceIds;
      const valorizationEdit = <ValorizationEdit>this.form.getRawValue();
      valorizationEdit.AgreementPriceIds = agreementPriceIds;
      valorizationEdit.PracticeRegionAdd = $event;
      valorizationEdit.SelectedPracticaRegionId = item.ValorizationItem
        .MedicalAppointmentProcedure.PracticeRegionAditionalId
        ? item.ValorizationItem.MedicalAppointmentProcedure
            .PracticeRegionAditionalId
        : item.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId;


      const refreshValorizationEdit =
        await this.valorizationService.addNewPracticeInValorizationEditFromAgreementPriceIds(
          valorizationEdit
        );


      await this.writeValue(refreshValorizationEdit);
      this.changeValorizationEdit();
      this.onAddPracticeClick.emit();
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Valorización"
      );
    } finally {
      this.loading = false;
    }
  }

  async addPracticeClick($event: PracticeRegionSearchReservation) {
    try {
      this.loading = true;
      const valorizationEdit = <ValorizationEdit>this.form.getRawValue();
      valorizationEdit.PracticeRegionAdd = $event;
      const refreshValorizationEdit =
        await this.valorizationService.addNewPracticeInValorizationEdit(
          valorizationEdit
        );
      await this.writeValue(refreshValorizationEdit);
      this.changeValorizationEdit();
      this.onAddPracticeClick.emit();
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Valorización"
      );
    } finally {
      this.loading = false;
    }
  }

  async refreshPracticeToParticular($event: ValorizationItemAmount) {
    try {
      this.loading = true;
      const valorizationEdit = <ValorizationEdit>this.form.getRawValue();
      const refreshValorizationEdit =
        await this.valorizationService.refreshItemInValorizationEditToParticular(
          valorizationEdit,
          $event
        );
      await this.writeValue(refreshValorizationEdit);
      this.changeValorizationEdit();
      this.onRefreshPracticeClick.emit();
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Valorización"
      );
    } finally {
      this.loading = false;
    }
  }

  async refreshPractice($event: ValorizationItemAmount) {
    try {
      this.loading = true;
      const valorizationEdit = <ValorizationEdit>this.form.getRawValue();
      const refreshValorizationEdit =
        await this.valorizationService.refreshItemInValorizationEdit(
          valorizationEdit,
          $event
        );
      await this.writeValue(refreshValorizationEdit);
      this.changeValorizationEdit();
      this.onRefreshPracticeClick.emit();
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Valorización"
      );
    } finally {
      this.loading = false;
    }
  }

  recalculateClick(): void {
    this.recalculating = true;
    this.onRecalculateClick.emit();
  }

  async writeValue(obj: ValorizationEdit) {
    if (obj) {
      this.receivedLabSampleEnabled =
        await this.receivedLabSampleService.getIsEnabled(
          obj.ValorizationAmount.Valorization.MedicalAppointmentId
        );

      this.form.patchValue({
        ValorizationAmount: obj.ValorizationAmount,
        DocumentationItems: obj.DocumentationItems,
      });

      if (obj.DocumentationItems) {
        this.totalDocumentationItems = obj.DocumentationItems.length;
      }


      if (this.recalculating) {
        this.valorizationAmountControl.setearAlicuota();
      }

      this.recalculating = false;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  verMuestras() {
    const valorizationEdit = <ValorizationEdit>this.form.getRawValue();
    const medicalAppointmentId =
      valorizationEdit.ValorizationAmount.Valorization.MedicalAppointmentId;

    this.dialog.open(ReceivedLabSampleDialogComponent, {
      width: "50%",
      data: {
        medicalAppointmentId: medicalAppointmentId,
        isAdmission: true,
      },
    });
  }

  async verNormas() {
    try {
      this.findingNormative = true;

      const valorizationEdit = <ValorizationEdit>this.form.getRawValue();
      const planId = valorizationEdit.ValorizationAmount.Plan.Id;
      const agreementPriceIds =
        valorizationEdit.ValorizationAmount.ValorizationItemsAmount.map(
          (v) => v.ValorizationItem.AgreementPriceId
        );
      const medicalAppointmentId =
        valorizationEdit.ValorizationAmount.Valorization.MedicalAppointmentId;

      const normative = await this.normativeService.getByParameters(
        agreementPriceIds,
        planId,
        medicalAppointmentId
      );

      this.dialog.open(NormativeDialogComponent, {
        width: "70%",
        data: normative,
      });
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Normas");
    } finally {
      this.findingNormative = false;
    }
  }
}
